import React, { useContext } from "react";
import clsx from "clsx";

import {
  NostoContext,
  type NostoContextProps,
} from "~/providers/NostoProvider";
import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";

import { FilterSectionProductStats } from "~/components/Filter/FilterSectionProductStats";
import CollectionHeroBanner from "~/components/Section/CollectionHeroBanner/CollectionHeroBanner";
import { type SectionsProps } from "~/components/Sections/withSections";

import { Breadcrumbs, Disclosure, Link } from "@bared/ui";

export type CollectionHeaderProps = {
  collectionHeroBannerContent?: {
    bannerTextColor?: "lightest" | "primary";
  } & SectionsProps;
  description?: string;
  paths: {
    title?: string;
    url?: string;
  }[];
  title: string;
  hideBreadcrumbAndTitle: boolean;
};

const Component = ({
  hideBreadcrumbAndTitle,
  title,
  collectionHeroBannerContent,
  paths,
}: CollectionHeaderProps) => {
  return (
    <CollectionHeroBanner
      {...collectionHeroBannerContent}
      collectionTitle={title}
    >
      {!hideBreadcrumbAndTitle && !!paths?.length && (
        <Breadcrumbs
          className={clsx(
            "absolute left-midi top-midi z-10 md:top-maxi xl:left-maxi",
            collectionHeroBannerContent?.bannerTextColor === "lightest" &&
              "text-white [&_a]:text-white [&_svg]:text-white",
          )}
          data-testid={"page-breadcrumb"}
        >
          {paths
            .map((path) => {
              if (!path) return;
              return path.url ? (
                <Link
                  href={path.url}
                  variant="link"
                  className={clsx(
                    collectionHeroBannerContent?.bannerTextColor ===
                      "lightest" && "hover:text-white",
                  )}
                >
                  {path.title}
                </Link>
              ) : (
                <span>{path.title}</span>
              );
            })
            .filter(Boolean)}
        </Breadcrumbs>
      )}
      <h1
        className="pt-tera text-left font-display text-[2rem] leading-[2.5rem] md:text-[2.75rem] md:leading-[3.25rem]"
        data-testid="collection-banner-page-title"
      >
        {!hideBreadcrumbAndTitle ? title : ""}
      </h1>
    </CollectionHeroBanner>
  );
};

export const CollectionHeader = ({
  description,
  paths,
  title,
  hideBreadcrumbAndTitle,
  collectionHeroBannerContent,
}: CollectionHeaderProps) => {
  const { enableNosto } = useRouteLoaderData("routes/$site");
  const { productCount } = useContext<NostoContextProps>(NostoContext);
  const productCountLabel = productCount > 1 ? " products" : " product";

  return (
    <div
      className={clsx(
        !collectionHeroBannerContent
          ? "relative mx-auto px-midi pt-tera"
          : "mx-zero px-zero",
        "relative flex flex-col",
      )}
    >
      {!collectionHeroBannerContent && !!paths?.length && (
        <Breadcrumbs
          className="absolute left-midi top-midi z-10 md:top-maxi xl:left-maxi"
          data-testid={"page-breadcrumb"}
        >
          {paths
            .map((path) => {
              if (!path) return;
              return path.url ? (
                <Link href={path.url} variant="link">
                  {path.title}
                </Link>
              ) : (
                <span>{path.title}</span>
              );
            })
            .filter(Boolean)}
        </Breadcrumbs>
      )}

      <div
        className={clsx(
          !description && !collectionHeroBannerContent
            ? "flex flex-col justify-between"
            : null,
        )}
      >
        {!collectionHeroBannerContent ? (
          <h1
            className="text-left font-display text-[2rem] leading-[2.5rem] md:text-[2.75rem] md:leading-[3.25rem]"
            data-testid="page-title"
          >
            {title}
          </h1>
        ) : (
          <Component
            hideBreadcrumbAndTitle={hideBreadcrumbAndTitle}
            title={title}
            collectionHeroBannerContent={collectionHeroBannerContent}
            paths={paths}
          />
        )}

        <div
          className={clsx(
            !collectionHeroBannerContent
              ? !description
                ? "w-[auto] flex-col md:flex-row md:items-end md:justify-end"
                : "w-[auto] flex-col justify-between md:flex-row md:items-end"
              : !description
                ? "mx-auto w-full lg:mt-midi lg:justify-end lg:px-midi"
                : "mx-auto w-full flex-col justify-between px-midi",
            "flex",
          )}
        >
          {description ? (
            <div
              data-testid="collection-description"
              className={clsx(
                "mt-midi flex flex-col items-start bg-neutral md:w-[700px]",
                description && !collectionHeroBannerContent && "md:pb-midi",
              )}
            >
              <Disclosure>{description}</Disclosure>
            </div>
          ) : null}
          <div className="flex items-center justify-end pb-midi">
            <div>
              {enableNosto ? (
                <div
                  data-testid="product-stats-label"
                  className="text-hint text-primary"
                >
                  {productCount > 0 ? productCount + productCountLabel : null}
                </div>
              ) : (
                <FilterSectionProductStats />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectionHeader;
